import ApiModel from 'models/api-model';

export class CollapsibleCheckboxListModel extends ApiModel({
    data: {
        items: [],
        type: null,
        masterChecked: false
    },
}) {

    constructor(props = {}) {
        super(props);
        this.type = props.type || null;
    }

    processResponse({ response, type }) {
        // Transform API response into checkbox format
        const items = response?.data.map(item => ({
            ...item,
            checked: 0,
        }));

        return {
            data: {
                items,
                type: type || this.type,
                masterChecked: false,
            },
        };
    }

    toggleItem({ index }) {
        const updatedItems = this.data?.items?.map((item, i) => {
            if (i === index && !item.disabled) {
                return { ...item, checked: item.checked ? 0 : 1 };
            }
            return item;
        });
        return this.mergeData({ items: updatedItems });
    }

    toggleMasterItem({ checked }) {
        const masterCheckbox = this.data.masterChecked;
        let updatedItems = this.data.items;
        if (!checked) {
            updatedItems = this.data.items.map(item => {
                if (!item.disabled) {
                    return { ...item, checked: masterCheckbox ? 0 : 1 };
                }
                return item;
            });
        }
        return this.mergeData({ items: updatedItems, masterChecked: checked });
    }

    appendCheckboxListData({ response }) {
        const existingData = this.data.items;
        const newDataList = response.data;
        if (!newDataList.length) {
            return this.mergeData({ items: [] });
        }
        const existingDataMap = new Map(existingData.map(item => [item.id, item]));
        const mergedArray = newDataList.map(item => {
            const existingItem = existingDataMap.get(item.id);
            if (existingItem) {
                // If item exists in current data, preserve its checked state
                return { ...item, checked: existingItem.checked };
            } else {
                // If item doesn't exist, use masterChecked to determine checked state
                return { ...item, checked: this.data.masterChecked ? 1 : 0 };
            }
        });
        return this.mergeData({ items: mergedArray });
    }

    appendCheckboxListDataViaSocket({ response }) {
        const data = this.data.items;
        const targetMap = new Map(data.map(item => [item.id, item]));
        const newDataList = response.data;
        newDataList.map(item => {
            if (!targetMap.has(item.id)) {
                targetMap.set(item.id, { ...item, checked: 0 });
            }
        })
        const mergedArray = Array.from(targetMap.values());
        return this.mergeData({ items: mergedArray });
    }

    mergeCheckboxListDataViaSocket({ response }) {
        const data = this.data.items;
        const responseList = response.data;

        // Early return if no response data
        if (!responseList || responseList.length === 0) {
            return this.mergeData({ items: data });
        }

        // Create efficient lookup structure
        const elementsToRemove = responseList.reduce((acc, { id, elementId }) => {
            if (!acc[id]) {
                acc[id] = new Set();
            }
            acc[id].add(elementId);
            return acc;
        }, {});

        // Process items with single pass
        const updatedItems = [];

        for (const item of data) {
            const { id, elementId: itemElementIds } = item;

            // Skip processing if no elements to remove for this item
            if (!elementsToRemove[id] || !Array.isArray(itemElementIds)) {
                updatedItems.push(item);
                continue;
            }

            // Filter elements efficiently
            const elementsToRemoveSet = elementsToRemove[id];
            const remainingElementIds = [];

            for (const elementId of itemElementIds) {
                if (!elementsToRemoveSet.has(elementId)) {
                    remainingElementIds.push(elementId);
                }
            }

            // Only add item if it has remaining elements
            if (remainingElementIds.length > 0) {
                updatedItems.push({
                    ...item,
                    elementId: remainingElementIds
                });
            }
        }

        return this.mergeData({ items: updatedItems });
    }

    removeRecordFromCheckboxListViaSocket({ response }) {
        let updatedItems = this.data.items;
        // Filter out the item with matching ID
        if (response.id) {
            updatedItems = updatedItems.filter(item => item.id !== response.id);
        }
        return this.mergeData({ items: updatedItems });
    }

    updateRecordFromCheckboxListViaSocket({ response }) {
        const data = this.data.items;
        const updateData = response.data;

        // Early return if no response data or no ID
        if (!updateData || !updateData.id) {
            return this.mergeData({ items: data });
        }

        // Update the item with matching ID
        const updatedItems = data.map(item => {
            if (item.id === updateData.id) {
                // Merge the existing item with the update data
                return {
                    ...item,
                    ...updateData
                };
            }
            return item;
        });

        return this.mergeData({ items: updatedItems });
    }

    // Getter methods
    get checkboxItems() {
        return this.data.items || [];
    }

    get masterChecked() {
        return this.data.masterChecked;
    }
}

